import { useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
import { Box, Card, CardActions, CardContent } from '@mui/material';
import BackButton from '../../../shared/components/ui/buttons/BackButton';
import Loading from '../../../shared/components/ui/feedback/Loading';
import SwipeImgAnimation from '../../../shared/components/ui/animations/SwipeImgAnimation';
import swipeHand from '../../../img/swipe_hand.png';
import ViewAllButton from '../../../shared/components/ui/buttons/ViewAllButton';
import apiClient from "../../../shared/api/axiosConfig";
import { useImageUpdate } from '../../../contexts/ImageUpdateContext';
import { getBaseUrl } from "../../../config/urls";

const PhotoConfirmation = () => {
    const location = useLocation();
    const photo = location.state?.photo as File;
    const navigate = useNavigate();
    const urlBase = getBaseUrl();
    const urlPicture = urlBase + "/api/picture";
    const { notifyImageUpdate } = useImageUpdate();

    const [isSwiping, setIsSwiping] = useState(false);
    const [clonePosition, setClonePosition] = useState({ x: 0, y: 0 });
    const [isUploading, setIsUploading] = useState(false);

    // スワイプ距離計算用
    const startYRef = useRef<number | null>(null);

    // スワイプ完了時の処理（直接アップロードを呼ぶ）
    const handleSwipeUp = async () => {
        setIsSwiping(false);
        setClonePosition({ x: 0, y: 0 });
        await handleImageUpload(); // ← state に依存せず直接呼ぶ
    };

    // 画像アップロード処理
    const handleImageUpload = async () => {
        if (!photo) return;

        setIsUploading(true);
        try {
            const token = localStorage.getItem("token");
            const roomid = sessionStorage.getItem("roomId");
            if (!roomid) throw new Error("Room ID not found");

            const formData = new FormData();
            formData.append('image', photo);
            formData.append('roomid', roomid);

            await apiClient.post(urlPicture, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            // アップロード成功時の処理
            notifyImageUpdate();
            localStorage.setItem('imageUploaded', 'true');
            localStorage.setItem('imageUploadTimestamp', Date.now().toString());

            navigate("../user/upload-success", { state: { fromUpload: true } });
        } catch (error) {
            console.error('POSTリクエストが失敗しました', error);
            navigate("../user/upload-failed", { state: { fromUpload: true } });
            alert('アップロードに失敗しました');
        } finally {
            setIsUploading(false);
        }
    };

    // スワイプイベント設定（PC/スマホ両対応）
    const swipeHandlers = useSwipeable({
        onSwiping: (eventData) => {
            const e = eventData.event as TouchEvent & MouseEvent;
            let clientX: number | undefined;
            let clientY: number | undefined;

            if ('touches' in e && e.touches.length > 0) {
                // スマホ
                clientX = e.touches[0].clientX;
                clientY = e.touches[0].clientY;
            } else if ('clientX' in e) {
                // PCマウス
                clientX = e.clientX;
                clientY = e.clientY;
            }

            if (clientX != null && clientY != null) {
                if (startYRef.current === null) startYRef.current = clientY;
                const distance = startYRef.current - clientY;

                // 30px以上でスワイプ認識（スクロール誤認防止）
                if (distance > 30) {
                    e.preventDefault(); // ブラウザの画像ドラッグ防止
                    setIsSwiping(true);
                    setClonePosition({ x: clientX, y: clientY - 100 });
                }
            }
        },
        onSwipedUp: () => {
            startYRef.current = null;
            handleSwipeUp();
        },
        onTouchEndOrOnMouseUp: () => {
            startYRef.current = null;
            setIsSwiping(false);
        },
        trackMouse: true,               // PCドラッグ対応
        preventScrollOnSwipe: true,     // スクロール抑止
    });

    return (
        <div className="photo-confirmation">
            <Box className="section">
                <Card
                    className="upload-card"
                    sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.5)',
                        boxShadow: 'none',
                        color: 'rgb(100, 90, 70)',
                        position: 'relative',
                    }}
                    {...swipeHandlers}
                >
                    <CardContent
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: '8px',
                        }}
                    >
                        <p className="title">Confirmation</p>
                        {photo ? (
                            <>
                                {/* 元画像 */}
                                <img
                                    src={URL.createObjectURL(photo)}
                                    alt="写真"
                                    draggable={false} // ←PCでのドラッグ抑止
                                    style={{
                                        opacity: isSwiping ? 0.3 : 1,
                                        transition: 'opacity 0.3s',
                                        width: '60%',
                                        aspectRatio: '1 / 1.1',
                                        objectFit: 'cover',
                                        objectPosition: '50% 50%',
                                    }}
                                />
                                {/* スワイプ中の複製画像 */}
                                {isSwiping && (
                                    <img
                                        src={URL.createObjectURL(photo)}
                                        alt="スワイプ中の画像"
                                        style={{
                                            position: 'absolute',
                                            left: clonePosition.x,
                                            top: clonePosition.y,
                                            transform: 'translate(-50%, -50%)',
                                            pointerEvents: 'none',
                                            zIndex: 100,
                                            width: '70%',
                                            height: 'auto',
                                        }}
                                    />
                                )}
                            </>
                        ) : (
                            "戻って写真を選んでください"
                        )}
                        <p className="process" style={{ fontWeight: 'bold' }}>※アップロード前に</p>
                        <p className="process" style={{ fontWeight: 'bold' }}>下記ご注意事項をご確認ください！</p>
                        <ul>
                            <li>公序良俗に反する写真や、新郎新婦や参列者を不快にさせる可能性のある写真でないこと</li>
                            <li>個人情報が記載されていないこと</li>
                            <li>著作権に注意し、他人の著作物などを許可なく使用しないこと</li>
                        </ul>
                    </CardContent>

                    <CardActions sx={{ display: 'flex', justifyContent: 'center', padding: '8px' }}>
                        <BackButton />
                    </CardActions>

                    <CardContent sx={{ display: 'flex', justifyContent: 'center', padding: '8px' }}>
                        <p className="attention" style={{ border: '5px double rgb(140, 110, 50)', padding: '6px' }}>
                            問題ない場合は写真を指またはマウスで上にスワイプしてください！
                        </p>
                    </CardContent>
                </Card>

                <SwipeImgAnimation src={swipeHand} animationDuration="3s" width='30vw' />
                <CardActions>
                    <ViewAllButton />
                </CardActions>
            </Box>

            {isUploading && <Loading />}
        </div>
    );
};

export default PhotoConfirmation;
